import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BsPlusCircle } from "react-icons/bs";
import { ActionIcon, Badge, Button, Card, Checkbox, Grid, Group, SimpleGrid, Space, Text, ThemeIcon, Title, Tooltip, } from "@mantine/core";
import { useSessionStorage } from "@mantine/hooks";
import { openConfirmModal } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { httpsCallable } from "firebase/functions";
import { IconEye, IconTable } from "@tabler/icons-react";
import type { TableColumn } from "react-data-table-component";
import { RefreshButton, RefreshButtonRef } from "../../components/customer_instant_search";
import { BaseResult, CustomClaims, ProductType, Service, ServiceStatusCode, ServiceType } from "../../types";
import type { PortfolioService } from "../../types";
import type { FilterItem, FilterSection } from "../../components/FilterModal";
import { FilterBadgeGroup } from "../../components/FilterBadges";
import GenerateExecutionServicesCSVModal from "../../components/generate_execution_services_csv_modal";
import GenerateServicesExcelModal from "../../components/generate_services_excel_modal";
import GlobalDpmExportButton from "../../components/global_dpm_export_button";
import ServicesInstantSearch from "../base_service/services_instant_search";
import { ConfigContext, MyUserDataContext } from "../../contexts";
import KCCModalButton from "../base_service/KCCModalButton";
import { useFunctions } from "../../helpers/firebaseContext";
import { formatPortfolioAmountWithoutFees } from "./portfolio_list_helpers";





interface ActionsComponentProps {
    serviceObj: Service;
}

const PORTFOLIO_MANAGEMENT_LABELS: Record<string, string> = {
    dpm: "DPM",
    "non-dpm": "Non-DPM",
    custody: "Custody",
};

const PORTFOLIO_MANAGEMENT_COLORS: Record<string, string> = {
    dpm: "blue",
    "non-dpm": "cyan",
    custody: "grape",
};

const getPortfolioManagementLabel = (portfolioType?: string) => {
    const normalizedPortfolioType = portfolioType?.trim().toLowerCase();

    if (!normalizedPortfolioType) {
        return "";
    }

    return PORTFOLIO_MANAGEMENT_LABELS[normalizedPortfolioType] ?? portfolioType;
};

const getPortfolioFallbackName = (portfolioDbId?: string) =>
    portfolioDbId
        ?.replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase())
        .replace(/\bDpm\b/g, "DPM")
        .replace(/\bNon DPM\b/g, "Non-DPM")
        .replace(/\bNondpm\b/g, "Non-DPM")
    ?? "";

const quoteTypesenseFilterValue = (value: string) => `\`${value.replace(/`/g, "\\`")}\``;

const getPortfolioOfferingName = (service: Service, portfolioService?: PortfolioService) => {
    const portfolioDbId = service.portfolioOpeningRequest?.portfolio_db_id?.trim();

    if (!portfolioDbId) {
        return "";
    }

    return portfolioService?.[portfolioDbId]?.name_en?.trim() || getPortfolioFallbackName(portfolioDbId);
};

const getPortfolioListTypeLabel = (service: Service, portfolioService?: PortfolioService) => {
    const offeringName = getPortfolioOfferingName(service, portfolioService);
    const managementLabel = getPortfolioManagementLabel(service.portfolioOpeningRequest?.portfolioType);

    if (!offeringName) {
        return managementLabel;
    }

    const normalizedOfferingName = offeringName.replace(/[^a-z0-9]/gi, "").toLowerCase();
    const normalizedManagementLabel = managementLabel.replace(/[^a-z0-9]/gi, "").toLowerCase();

    if (!normalizedManagementLabel || normalizedOfferingName.includes(normalizedManagementLabel)) {
        return offeringName;
    }

    return `${offeringName} / ${managementLabel}`;
};

const getPortfolioListColumns = (portfolioService?: PortfolioService): TableColumn<Service>[] => [
    {
        name: "Portfolio Type",
        id: "portfolioOpeningRequest.portfolio_db_id",
        selector: (row) => getPortfolioListTypeLabel(row, portfolioService),
        cell: (row) => {
            const offeringName = getPortfolioOfferingName(row, portfolioService);
            const managementType = row.portfolioOpeningRequest?.portfolioType?.trim().toLowerCase();
            const managementLabel = getPortfolioManagementLabel(row.portfolioOpeningRequest?.portfolioType);

            if (!offeringName && !managementLabel) {
                return <span>—</span>;
            }

            return (
                <Group gap="xs" wrap="wrap">
                    {offeringName && <span>{offeringName}</span>}
                    {managementLabel && (
                        <Badge color={managementType ? PORTFOLIO_MANAGEMENT_COLORS[managementType] : undefined} variant="light">
                            {managementLabel}
                        </Badge>
                    )}
                </Group>
            );
        },
        width: "220px",
        wrap: true,
    },
    {
        name: "Amount (without fees)",
        id: "amountWithoutFees",
        selector: formatPortfolioAmountWithoutFees,
        minWidth: "190px",
        wrap: true,
    },
];

// Filter selections live in sessionStorage so they survive a page refresh and
// navigating to a portfolio's details and back (they reset when the tab closes).
const PORTFOLIO_TYPE_FILTER_STORAGE_KEY = "portfolios_filter_portfolio_db_ids";
const PORTFOLIO_MANAGEMENT_FILTER_STORAGE_KEY = "portfolios_filter_management_types";

const deserializeStringArray = (raw: string | undefined): string[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
    } catch (error) {
        console.error("Error reading portfolio filters from sessionStorage", error);
        return [];
    }
};

const toFilterItems = (
    options: { value: string; label: string }[],
    selectedValues: string[],
): FilterItem[] => {
    const knownValues = new Set(options.map((option) => option.value));
    return [
        ...options.map((option) => ({
            value: option.value,
            label: option.label,
            count: 0,
            isRefined: selectedValues.includes(option.value),
        })),
        // Keep pills for stored values that are no longer in the options so they can still be removed.
        ...selectedValues
            .filter((value) => !knownValues.has(value))
            .map((value) => ({ value, label: value, count: 0, isRefined: true })),
    ];
};

const toggleFilterValue = (
    value: string,
    setValues: Dispatch<SetStateAction<string[]>>
) => {
    setValues((current) =>
        current.includes(value)
            ? current.filter((currentValue) => currentValue !== value)
            : [...current, value]
    );
};


const RenderActions = ({ serviceObj }: ActionsComponentProps) => {


    const navigate = useNavigate();

    return <Group>
        <ThemeIcon color="blue" variant="light" onClick={() => {
            navigate("/portfolios/" + serviceObj.id, { state: serviceObj })
        }}><IconEye size={16} /></ThemeIcon>
    </Group>

}
const ServicesList = ({ serviceType }: { serviceType: ServiceType }) => {


    const userContext = useContext(MyUserDataContext);
    const configContext = useContext(ConfigContext);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    let userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};

    const [selectedRows, setSelectedRows] = useState<Service[]>([]);
    const [selectedPortfolioDbIds, setSelectedPortfolioDbIds] = useSessionStorage<string[]>({
        key: PORTFOLIO_TYPE_FILTER_STORAGE_KEY,
        defaultValue: [],
        getInitialValueInEffect: false,
        deserialize: deserializeStringArray,
    });
    const [selectedManagementTypes, setSelectedManagementTypes] = useSessionStorage<string[]>({
        key: PORTFOLIO_MANAGEMENT_FILTER_STORAGE_KEY,
        defaultValue: [],
        getInitialValueInEffect: false,
        deserialize: deserializeStringArray,
    });
    const [csvModalOpened, setCsvModalOpened] = useState(false);
    const [excelModalOpened, setExcelModalOpened] = useState(false);
    const [sendingContracts, setSendingContracts] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const functions = useFunctions();
    const sendGeneratedContractPDF = httpsCallable<{ productRef: string, customerID: string, productType: ProductType }, BaseResult>(functions, "sendGeneratedContractPDF");

    const { refreshServicesIn } = location.state || {};


    const refreshRef = useRef<RefreshButtonRef | null>(null);
    const portfolioListColumns = useMemo(
        () => getPortfolioListColumns(configContext.portfolio_service),
        [configContext.portfolio_service],
    );
    const portfolioTypeOptions = useMemo(() => {
        return Object.entries(configContext.portfolio_service ?? {})
            .sort((a, b) => {
                const positionA = a[1].position ?? 0;
                const positionB = b[1].position ?? 0;

                if (positionA !== positionB) {
                    return positionA - positionB;
                }

                return (a[1].name_en || a[0]).localeCompare(b[1].name_en || b[0]);
            })
            .map(([value, portfolio]) => ({
                value,
                label: portfolio.name_en || getPortfolioFallbackName(value),
            }));
    }, [configContext.portfolio_service]);
    const managementTypeOptions = useMemo(() => {
        const managementTypes = new Set<string>();

        Object.values(configContext.portfolio_service ?? {}).forEach((portfolio) => {
            portfolio.management_options?.forEach((option) => {
                if (option.portfolio_type) {
                    managementTypes.add(option.portfolio_type);
                }
            });
        });

        return Array.from(managementTypes)
            .sort((a, b) => getPortfolioManagementLabel(a).localeCompare(getPortfolioManagementLabel(b)))
            .map((value) => ({
                value,
                label: getPortfolioManagementLabel(value),
            }));
    }, [configContext.portfolio_service]);
    const portfolioFilterSections = useMemo<FilterSection[]>(() => [
        {
            id: "portfolioType",
            title: "Portfolio Type",
            type: "custom",
            selectedCount: selectedPortfolioDbIds.length,
            onClear: () => setSelectedPortfolioDbIds([]),
            renderContent: () => (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs" verticalSpacing={6}>
                    {portfolioTypeOptions.map((option) => (
                        <Checkbox
                            key={option.value}
                            color="green"
                            checked={selectedPortfolioDbIds.includes(option.value)}
                            onChange={() => toggleFilterValue(option.value, setSelectedPortfolioDbIds)}
                            label={option.label}
                        />
                    ))}
                </SimpleGrid>
            ),
        },
        {
            id: "portfolioManagementType",
            title: "Management Option",
            type: "custom",
            selectedCount: selectedManagementTypes.length,
            onClear: () => setSelectedManagementTypes([]),
            renderContent: () => (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs" verticalSpacing={6}>
                    {managementTypeOptions.map((option) => (
                        <Checkbox
                            key={option.value}
                            color="green"
                            checked={selectedManagementTypes.includes(option.value)}
                            onChange={() => toggleFilterValue(option.value, setSelectedManagementTypes)}
                            label={option.label}
                        />
                    ))}
                </SimpleGrid>
            ),
        },
    ], [managementTypeOptions, portfolioTypeOptions, selectedManagementTypes, selectedPortfolioDbIds]);
    const portfolioFilterBadges = (
        <>
            <FilterBadgeGroup
                items={toFilterItems(portfolioTypeOptions, selectedPortfolioDbIds)}
                color="green"
                refine={(value) => toggleFilterValue(value, setSelectedPortfolioDbIds)}
            />
            <FilterBadgeGroup
                items={toFilterItems(managementTypeOptions, selectedManagementTypes)}
                color="grape"
                refine={(value) => toggleFilterValue(value, setSelectedManagementTypes)}
            />
        </>
    );
    const portfolioFilterClauses = useMemo(() => {
        return [
            selectedPortfolioDbIds.length
                ? `portfolioOpeningRequest.portfolio_db_id:=[${selectedPortfolioDbIds.map(quoteTypesenseFilterValue).join(",")}]`
                : undefined,
            selectedManagementTypes.length
                ? `portfolioOpeningRequest.portfolioType:=[${selectedManagementTypes.map(quoteTypesenseFilterValue).join(",")}]`
                : undefined,
        ].filter(Boolean) as string[];
    }, [selectedManagementTypes, selectedPortfolioDbIds]);

    const handleExternalRefresh = () => {
        if (refreshRef.current) {
            refreshRef.current.refresh();
        }
    };

    const sendSelectedContracts = async () => {

        // skip services with no service ID and no cusotmer ID and that are not "done"
        const intitialRowsToSend = selectedRows.filter((service) => service.id && service.customerObj?.id && service?.tags?.serviceStatus.code === ServiceStatusCode.done);

        //exclude rows with documentSent = true
        const rowsToSend = intitialRowsToSend?.filter((selectedRow) => !selectedRow?.documentSent)

        const alreadySentCount = intitialRowsToSend?.length - rowsToSend?.length;

        if (alreadySentCount) {

            notifications.show({
                title: "Already Sent to clients",
                message: `${alreadySentCount} Clients already received their document, skipping...`,
                color: "orange",
            });
        }

        const skippedCount = selectedRows.length - rowsToSend.length;

        if (!rowsToSend.length) {
            notifications.show({
                message: "No selected portfolio",
                color: "red",
            });
            return;
        }

        setSendingContracts(true);
        try {

            const results = await Promise.allSettled(rowsToSend.map(async (service) => {
                const customerID = service.customerObj!.id!;
                const result = await sendGeneratedContractPDF({
                    productRef: `customers/${customerID}/services/${service.id}`,
                    customerID,
                    productType: "services",
                });

                if (result.data.status === "error") {
                    throw new Error(result.data.message || "Failed to send contract");
                }
            }));

            // if promise returned failed for wthatever reason , add to failed count
            const failedCount = results.filter((result) => result.status === "rejected").length;
            const sentCount = rowsToSend.length - failedCount;

            notifications.show({
                title: failedCount ? 'Some messges failed to send' : "Messages Sent",
                message: ` ${sentCount} contract(s) sent${failedCount ? `,Please check the error and try again later : ${failedCount} failed` : ""}${skippedCount ? `, ${skippedCount} skipped` : ""}`,
                color: failedCount ? "red" : "green",
            });
        } finally {
            setSendingContracts(false);
        }
    };


    useEffect(() => {
        if (refreshServicesIn) {
            const timer = setTimeout(() => {
                // Action to perform after delay
                console.log('Refreshing services...');
                handleExternalRefresh();
                // Place your code here to refresh services or any other intended action
            }, refreshServicesIn);

            // Clean up the timer when the component unmounts or if the value of refreshServicesIn changes
            return () => clearTimeout(timer);
        }
    }, [refreshServicesIn]); // Dependency array, re-run effect if refreshServicesIn changes


    const searchableServices = useMemo(() => <ServicesInstantSearch
        setSelectedRows={setSelectedRows}
        selectable={true}
        selectedRows={selectedRows}
        renderTags={true}
        customFilters={`serviceType:[portfolio]`}
        navigateDetails={(obj: any) => {


            // return navigate("/services/" + obj.id)



        }
        }
        ActionsComponent={RenderActions}
        indexName={import.meta.env.VITE_TYPESENSE_SERVICES_COLLECTION!}

        additionalChildren={<RefreshButton ref={refreshRef} />}
        serviceType={serviceType}
        additionalColumns={portfolioListColumns}
        additionalFilterSections={portfolioFilterSections}
        additionalFilterBadges={portfolioFilterBadges}
        filterClauses={portfolioFilterClauses}
        hydratePortfolioRequests



    />,
        // eslint-disable-next-line 
        [portfolioFilterClauses, portfolioFilterSections, portfolioListColumns, selectedManagementTypes, selectedPortfolioDbIds, selectedRows, serviceType]);



    return (
        <>
            <Group justify="space-between">
                <Title>Portfolios</Title>
                <Group>



                    {userClaims.admin &&
                        <KCCModalButton />
                    }

                    {(userClaims?.admin || userClaims.cr) && <Button
                        color="red"
                        loading={sendingContracts}
                        disabled={selectedRows.length === 0}
                        onClick={() => openConfirmModal({
                            title: <Title order={2} c="red">Send contracts to clients</Title>,
                            children: (
                                <>
                                    <Title order={3} c="red">
                                        Are you sure you want to send contracts for {selectedRows.length} selected portfolio service(s)?
                                    </Title>
                                    <Text c="red" td="underline" fw="bold">
                                        This action cannot be reverted
                                    </Text>
                                </>
                            ),
                            labels: { confirm: "Send", cancel: "Cancel" },
                            confirmProps: { color: "red", loading: sendingContracts },
                            cancelProps: { loading: sendingContracts },
                            onConfirm: sendSelectedContracts,
                        })}
                    >
                        Send contracts to {selectedRows.length} client(s)
                    </Button>}


                    <GlobalDpmExportButton />

                    <Tooltip label="Export Excel" withArrow>
                        <ActionIcon
                            onClick={() => setExcelModalOpened(true)}
                            size="lg"
                            variant="filled"
                        >
                            <IconTable size={16} />
                        </ActionIcon>
                    </Tooltip>

                    {(userClaims?.admin || userClaims.cr) &&
                        <Button
                            leftSection={<BsPlusCircle />}
                            onClick={() => {
                                navigate("/portfolios/add_portfolio");
                            }}
                        >Add portfolio</Button>}







                </Group>
            </Group >
            <Space h="xl" />
            {/* <Card shadow="sm" p="lg" radius="md" withBservice> */}
            <Card shadow="sm" p="lg" radius="md" withBorder>
                <Grid justify="space-between">
                    <Grid.Col>

                    </Grid.Col>
                    <Grid.Col>
                        {searchableServices}
                    </Grid.Col>
                </Grid>
            </Card>

            <GenerateExecutionServicesCSVModal
                opened={csvModalOpened}
                setOpened={setCsvModalOpened}
            />
            <GenerateServicesExcelModal
                opened={excelModalOpened}
                setOpened={setExcelModalOpened}
            />

        </>
    );
};


const Portfolios = () => {
    return (
        <main style={{ padding: "1rem 0" }}>
            <ServicesList serviceType={"portfolio"} />

        </main>
    );
};

export default Portfolios;
